import type { CheckoutRequest } from "@/types/woocommerce.types";

import "../../../plugins";

import {
  initialize_cart_session,
  add_product_to_cart,
  clear_cart,
  get_cart_token,
  apply_coupon,
  get_coupon,
} from "../../../services/woocommerce/cart-service";
import {
  find_variant_by_id,
  collect_billing_data,
} from "@/lib/browser/checkout-utils";
import {
  setup_billing_country_restriction_handler,
  check_url_region_restrictions,
  show_loading_overlay,
  hide_loading_overlay,
  hide_all_payment_sections,
  show_payment_section,
  show_checkout_error,
  hide_all_checkout_errors,
  reset_place_order_button,
  show_place_order_processing_in_button,
} from "./checkout-ui-actions.js";
import { get_total_from_display } from "./purchase-summary-section.scripts.js";
import {
  get_selected_payment_method,
  initialize_selected_gateway_ui,
} from "./payment-method-section.scripts.js";
import {
  get_plugin_by_id,
  is_gateway_allowed,
} from "../../../plugins/payment-plugin-registry";

import {
  log_checkout_event,
  report_error,
  SENTRY_ERROR_CATEGORIES,
} from "@/lib/observability/sentry-reporting";
import { get_runtime_config } from "@/lib/config/runtime-config";

import {
  validate_all_fields,
  setup_real_time_field_validation,
} from "@/lib/validation/form-validation";

import {
  show_coupon_error,
  hide_all_coupon_errors,
} from "./checkout-ui-actions.js";
import {
  PAYMENT_ERROR_CODES,
  COUPON_ERROR_CODES,
} from "@/lib/constants/payment-error-constants";
import type {
  PAYMENT_ERROR_TYPES,
  COUPON_ERROR_TYPES,
} from "@/lib/constants/payment-error-constants";
import { handle_remove_coupon } from "./purchase-summary-section.scripts.js";

const ERROR_CATEGORIES = {
  COUPON: "COUPON",
  CHECKOUT: "CHECKOUT",
} as const;
type ErrorCategory = (typeof ERROR_CATEGORIES)[keyof typeof ERROR_CATEGORIES];

/**
 * Sets checkout UI into processing state
 * @returns {void}
 */
const set_processing_state = (): void => {
  show_place_order_processing_in_button();

  show_loading_overlay();
};

/**
 * Resets checkout UI from processing state
 * @returns {void}
 */
const reset_processing_state = (): void => {
  reset_place_order_button();

  hide_loading_overlay();
};

type UiError = {
  type: ErrorCategory;
  code: COUPON_ERROR_TYPES | PAYMENT_ERROR_TYPES;
};

/**
 * Validates required DOM and form states before checkout submission
 * @returns {{success: true, place_order_btn: HTMLButtonElement, checkout_form: HTMLFormElement, account_selection_form: HTMLFormElement} | {success: false, error: UiError}} Validation result
 */
const validate_prerequisites = ():
  | {
      success: true;
      place_order_btn: HTMLButtonElement;
      checkout_form: HTMLFormElement;
      account_selection_form: HTMLFormElement;
    }
  | { success: false; error: UiError } => {
  const place_order_btn = document.getElementById(
    "place-order-btn",
  ) as HTMLButtonElement | null;
  const checkout_form = document.getElementById(
    "checkout-form",
  ) as HTMLFormElement | null;
  const account_selection_form = document.getElementById(
    "account-selection-form",
  ) as HTMLFormElement | null;

  if (!place_order_btn || !checkout_form || !account_selection_form) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.CHECKOUT_UI_MISSING_ELEMENTS,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.UI_RENDERING,
      functionName: "validate_prerequisites",
      message: "Required checkout DOM elements missing",
      severity: "error",
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.CHECKOUT_UI_MISSING_ELEMENTS,
      },
    };
  }

  const is_form_valid = validate_all_fields(checkout_form);
  const is_account_form_valid = validate_all_fields(account_selection_form);

  if (!is_form_valid) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.CHECKOUT_FORM_VALIDATION_ERROR,
    });
  }

  if (!is_account_form_valid) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.ACCOUNT_SELECTION_VALIDATION_ERROR,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.VALIDATION,
      functionName: "validate_prerequisites",
      message: "Form validation failed",
      severity: "warning",
      businessContext: {
        checkout_form_valid: is_form_valid,
        account_selection_form_valid: is_account_form_valid,
      },
    });
  }

  if (!is_form_valid || !is_account_form_valid) {
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: !is_form_valid
          ? PAYMENT_ERROR_CODES.CHECKOUT_FORM_VALIDATION_ERROR
          : PAYMENT_ERROR_CODES.ACCOUNT_SELECTION_VALIDATION_ERROR,
      },
    };
  }

  const cart_token = get_cart_token();

  if (!cart_token) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.SESSION_EXPIRED_ERROR,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.STATE_MANAGEMENT,
      functionName: "validate_prerequisites",
      message: "Missing cart token (session expired)",
      severity: "error",
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.SESSION_EXPIRED_ERROR,
      },
    };
  }

  const url_params = new URLSearchParams(window.location.search);
  const variation_id = url_params.get("variation_id");

  if (!variation_id) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PRODUCT_SELECTION_ERROR,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.VALIDATION,
      functionName: "validate_prerequisites",
      message: "Missing variation_id in URL",
      severity: "warning",
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PRODUCT_SELECTION_ERROR,
      },
    };
  }

  return {
    success: true,
    place_order_btn,
    checkout_form,
    account_selection_form,
  };
};

/**
 * Validates the selected product variant from URL
 * @param {{variation_id: string}} params - Variation identifier from URL
 * @returns {Promise<{success: true, variant_id: number} | {success: false, error: UiError}>} Validation result
 */
const validate_variant = async ({
  variation_id,
}: {
  variation_id: string;
}): Promise<
  { success: true; variant_id: number } | { success: false; error: UiError }
> => {
  let selected_variant: Awaited<ReturnType<typeof find_variant_by_id>> | null;
  try {
    selected_variant = await find_variant_by_id({
      variant_id: Number(variation_id),
    });
  } catch (error) {
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.VARIANT_FETCH_FAILED,
      },
    };
  }

  if (!selected_variant) {
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.VARIATION_ID_INVALID,
      },
    };
  }
  return { success: true, variant_id: selected_variant.variant_id };
};

/**
 * Validates and applies coupon if present in URL
 * @param {{variant_id: number}} params - Current variant id for context
 * @returns {Promise<{success: true} | {success: false, error: UiError}>} Validation result
 */
const validate_coupon_if_present = async ({
  variant_id,
}: {
  variant_id: number;
}): Promise<{ success: true } | { success: false; error: UiError }> => {
  const url_params = new URLSearchParams(window.location.search);
  const coupon_code = url_params.get("coupon");

  if (!coupon_code) return { success: true };

  const email_input = document.getElementById(
    "billing_email",
  ) as HTMLInputElement | null;

  const user_email = email_input?.value?.trim();
  const context = {
    cart_total: get_total_from_display(),
    existing_coupons: [],
    cart_product_id: undefined as number | undefined,
    cart_variant_id: variant_id,
    email: user_email,
  };

  let validation_response: Awaited<ReturnType<typeof get_coupon>>;
  try {
    validation_response = await get_coupon(coupon_code, context);
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
      functionName: "validate_coupon_if_present",
      message: "Coupon validation request failed",
      severity: "error",
      network: {
        operation: "get_coupon",
        headers: { "Cart-Token": get_cart_token() },
        payload: { coupon_code, context },
      },
      error,
    });

    show_coupon_error({
      error_type: COUPON_ERROR_CODES.COUPON_NETWORK_ERROR,
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.COUPON,
        code: COUPON_ERROR_CODES.COUPON_NETWORK_ERROR,
      },
    };
  }

  if (
    !validation_response.success &&
    validation_response.error_code !== undefined
  ) {
    const error_type = validation_response.error_code;
    show_coupon_error({ error_type });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.VALIDATION,
      functionName: "validate_coupon_if_present",
      message: "Coupon validation failed",
      severity: "warning",
      businessContext: {
        coupon_code,
        variant_id,
        cart_total: context.cart_total,
      },
    });

    return {
      success: false,
      error: { type: ERROR_CATEGORIES.COUPON, code: error_type },
    };
  }
  try {
    await apply_coupon({ coupon_code });
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
      functionName: "validate_coupon_if_present",
      message: "Apply coupon request failed",
      severity: "error",
      network: {
        operation: "apply_coupon",
        headers: { "Cart-Token": get_cart_token() },
        payload: { coupon_code },
      },
      error,
    });
    show_coupon_error({
      error_type: COUPON_ERROR_CODES.COUPON_APPLY_FAILED,
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.COUPON,
        code: COUPON_ERROR_CODES.COUPON_APPLY_FAILED,
      },
    };
  }
  return { success: true };
};

/**
 * Builds the WooCommerce checkout request payload
 * @param {{billing_data: NonNullable<CheckoutRequest["billing_address"]>, payment_method: string}} params - Billing data and selected method
 * @returns {CheckoutRequest} Order request payload
 */
const build_order_request = ({
  billing_data,
  payment_method,
}: {
  billing_data: NonNullable<CheckoutRequest["billing_address"]>;
  payment_method: string;
}): CheckoutRequest => {
  return {
    billing_address: billing_data,
    shipping_address: billing_data,
    payment_method,
    payment_data: [],
  };
};

/**
 * Processes a free order using the `free` payment plugin
 * @param {{order_data: CheckoutRequest}} params - Order request data
 * @returns {Promise<{success: true, order_id: number | string} | {success: false, error: UiError}>} Processing result
 */
const process_free_order_payment = async ({
  order_data,
}: {
  order_data: CheckoutRequest;
}): Promise<
  | { success: true; order_id: number | string }
  | { success: false; error: UiError }
> => {
  const plugin = get_plugin_by_id("free");

  if (!plugin) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_UNAVAILABLE,
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_UNAVAILABLE,
      },
    };
  }

  let payment_result: Awaited<ReturnType<typeof plugin.process_payment>>;
  try {
    payment_result = await plugin.process_payment(order_data);
  } catch (error) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NETWORK_ERROR,
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NETWORK_ERROR,
      },
    };
  }

  if (!payment_result.success && payment_result.error_code) {
    show_checkout_error({
      error_type: payment_result.error_code,
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: payment_result.error_code,
      },
    };
  }

  const order_id = payment_result.order_id;

  if (!order_id) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.ORDER_CREATION_FAILED,
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.ORDER_CREATION_FAILED,
      },
    };
  }

  try {
    const res = await fetch(`/api/woocommerce/order?order_id=${order_id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
    });

    if (!res.ok) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      });
      return {
        success: false,
        error: {
          type: ERROR_CATEGORIES.CHECKOUT,
          code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
        },
      };
    }

    const order_data: { order_amount?: number } = await res.json();
    const order_total = Number(order_data?.order_amount);

    if (isNaN(order_total)) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.VALIDATION_ERROR,
      });
      return {
        success: false,
        error: {
          type: ERROR_CATEGORIES.CHECKOUT,
          code: PAYMENT_ERROR_CODES.VALIDATION_ERROR,
        },
      };
    }

    if (order_total > 0) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      });
      return {
        success: false,
        error: {
          type: ERROR_CATEGORIES.CHECKOUT,
          code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
        },
      };
    }
  } catch {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      },
    };
  }

  return { success: true, order_id };
};

/**
 * Processes a paid order via selected payment plugin
 * @param {{order_data: CheckoutRequest, payment_method: "nowpayments_gateway"|"paytiko"|"stripe"|"rave"|"free"|"myfatoorah_v2"|"kora"}} params - Order data and method
 * @returns {Promise<{success: true, order_id: number | string} | {success: false, error: UiError}>} Processing result
 */
const process_paid_order_payment = async ({
  order_data,
  payment_method,
}: {
  order_data: CheckoutRequest;
  payment_method:
    | "nowpayments_gateway"
    | "paytiko"
    | "stripe"
    | "rave"
    | "free"
    | "myfatoorah_v2"
    | "kora";
}): Promise<
  | { success: true; order_id: number | string }
  | { success: false; error: UiError }
> => {
  if (!payment_method) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_METHOD_NOT_SELECTED,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.VALIDATION,
      functionName: "process_paid_order_payment",
      message: "No payment method selected",
      severity: "warning",
      businessContext: { order_data },
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_METHOD_NOT_SELECTED,
      },
    };
  }

  const plugin = get_plugin_by_id(payment_method);

  const country_code = order_data.billing_address?.country?.toUpperCase() || "";

  if (!plugin) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_UNAVAILABLE,
    });
    report_error({
      category: SENTRY_ERROR_CATEGORIES.THIRD_PARTY_INTEGRATION,
      functionName: "process_paid_order_payment",
      message: "Payment gateway unavailable",
      severity: "warning",
      businessContext: { payment_method, country_code },
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_UNAVAILABLE,
      },
    };
  }

  if (!is_gateway_allowed(payment_method, country_code)) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NOT_ALLOWED_FOR_COUNTRY,
    });

    report_error({
      category: SENTRY_ERROR_CATEGORIES.THIRD_PARTY_INTEGRATION,
      functionName: "process_paid_order_payment",
      message: "Payment gateway not allowed for country",
      severity: "warning",
      businessContext: { payment_method, country_code },
    });

    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NOT_ALLOWED_FOR_COUNTRY,
      },
    };
  }

  let result: Awaited<ReturnType<typeof plugin.process_payment>>;

  try {
    result = await plugin.process_payment({
      ...order_data,
      payment_method,
    });
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.THIRD_PARTY_INTEGRATION,
      functionName: "process_paid_order_payment",
      message: "Payment gateway process_payment threw",
      severity: "error",
      businessContext: { payment_method, order_data },
      error,
    });
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NETWORK_ERROR,
    });
    return {
      success: false,
      error: {
        type: ERROR_CATEGORIES.CHECKOUT,
        code: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_NETWORK_ERROR,
      },
    };
  }
  if (result.success === false) {
    console.log("result.error_code", result.error_code);

    const error_code: PAYMENT_ERROR_TYPES =
      (result.error_code as PAYMENT_ERROR_TYPES) ||
      PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR;

    if (error_code === PAYMENT_ERROR_CODES.COUPON_LIMIT_REACHED) {
      handle_remove_coupon();

      show_coupon_error({
        error_type: COUPON_ERROR_CODES.COUPON_LIMIT_REACHED,
      });
    } else {
      show_checkout_error({
        error_type: error_code,
        order_id: (result as any)?.order_id,
      });
    }

    const woocommerce_errors: PAYMENT_ERROR_TYPES[] = [
      PAYMENT_ERROR_CODES.COUPON_LIMIT_REACHED,
      PAYMENT_ERROR_CODES.KORA_CANCELLED_BY_USER,
    ];

    const is_small_error = woocommerce_errors.includes(error_code);

    report_error({
      category: SENTRY_ERROR_CATEGORIES.THIRD_PARTY_INTEGRATION,
      functionName: "process_paid_order_payment",
      message: "Payment gateway returned failure",
      severity: is_small_error ? "warning" : "error",
      businessContext: {
        payment_method,
        order_data: JSON.stringify(order_data),
        error_code,
      },
    });

    return {
      success: false,
      error: { type: ERROR_CATEGORIES.CHECKOUT, code: error_code },
    };
  }

  return { success: true, order_id: result.order_id! };
};

/**
 * Handles successful payment: clears cart and redirects
 * @param {{order_id?: number | string}} params - Completed order id
 * @returns {Promise<void>}
 */
const handle_success = async ({
  order_id,
  payment_method,
}: {
  order_id: number | string;
  payment_method: string;
}): Promise<void> => {
  if (!order_id) return;
  try {
    await clear_cart();
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
      functionName: "handle_success",
      message: "Failed to clear cart after success",
      severity: "warning",
      error,
    });
  }

  const redirect_path =
    get_runtime_config().success_redirect_path || "thank-you";

  const stop_redirect =
    payment_method === "nowpayments_gateway" || payment_method === "paytiko";

  if (!stop_redirect && redirect_path) {
    const lang = document.documentElement.lang;
    const success_redirect_path = `/${redirect_path}?order_id=${order_id}`;
    const localized_redirect_path =
      lang === "en"
        ? success_redirect_path
        : `/${lang}${success_redirect_path}`;
    window.location.href = localized_redirect_path;
  }
};

/**
 * Orchestrates full checkout submission and payment flow
 * @returns {Promise<void>}
 */
const handle_checkout_form_submission = async (): Promise<void> => {
  try {
    // Step 1: Initialize checkout process
    hide_all_checkout_errors();
    hide_all_coupon_errors();

    log_checkout_event("checkout", "Checkout submission started");

    // Step 2: Validate prerequisites
    const prerequisites = validate_prerequisites();

    if (!prerequisites.success) return;

    const { checkout_form } = prerequisites;

    // Step 3: Set processing state
    set_processing_state();

    log_checkout_event("checkout", "Processing state set");

    // Step 4: Extract and validate variation ID
    const checkout_form_data = new FormData(checkout_form);
    const url_params = new URLSearchParams(window.location.search);
    const variation_id = url_params.get("variation_id")!;

    if (!variation_id || isNaN(Number(variation_id))) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.VARIATION_ID_INVALID,
      });
      reset_processing_state();
      log_checkout_event("checkout", "Variation id invalid", { variation_id });
      return;
    }

    // Step 5: Validate variant availability
    const variant_result = await validate_variant({ variation_id });

    if (!variant_result.success) {
      show_checkout_error({
        error_type: variant_result.error.code,
      });

      reset_processing_state();
      log_checkout_event("checkout", "Variant validation failed", {
        variation_id,
      });
      return;
    }

    const { variant_id } = variant_result;

    // Step 6: Add product to cart
    try {
      await clear_cart();
      await add_product_to_cart({ variant_id });
      log_checkout_event("checkout", "Product added to cart", { variant_id });
    } catch (error) {
      report_error({
        category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
        functionName: "handle_checkout_form_submission",
        message: "Add product to cart failed",
        severity: "error",
        network: {
          operation: "add_product_to_cart",
          headers: { "Cart-Token": get_cart_token() },
          payload: { variant_id },
        },
        error,
      });
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.CART_UPDATE_FAILED,
      });
      reset_processing_state();
      return;
    }

    // Step 7: Validate coupon if present
    const coupon_result = await validate_coupon_if_present({ variant_id });

    if (coupon_result && !coupon_result.success) {
      reset_processing_state();
      log_checkout_event("checkout", "Coupon validation failed", {
        variant_id,
      });
      return;
    }

    // Step 8: Collect billing data
    const billing_data = collect_billing_data({
      form_data: checkout_form_data,
    });
    if (!billing_data) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.BILLING_DATA_COLLECTION_FAILED,
      });
      reset_processing_state();
      log_checkout_event("checkout", "Billing data collection failed");
      return;
    }

    // Step 9: Prepare order request
    const cart_total = get_total_from_display();
    const order_is_free = cart_total <= 0;

    const payment_method =
      (get_selected_payment_method() as
        | "nowpayments_gateway"
        | "paytiko"
        | "stripe"
        | "rave"
        | "free"
        | "myfatoorah_v2"
        | "kora") || "free";

    const order_request = build_order_request({
      billing_data: billing_data!,
      payment_method,
    });

    // Step 10: Process payment
    const payment_res = order_is_free
      ? await process_free_order_payment({ order_data: order_request })
      : await process_paid_order_payment({
          order_data: order_request,
          payment_method,
        });

    if (!payment_res.success) {
      reset_processing_state();

      log_checkout_event("checkout", "Payment failed", {
        payment_method,
        order_is_free,
      });
      return;
    }

    // Step 11: Finalize checkout
    await handle_success({
      order_id: payment_res.order_id,
      payment_method: payment_method,
    });

    log_checkout_event("checkout", "Payment succeeded", {
      order_id: payment_res.order_id,
      payment_method,
    });

    reset_processing_state();
  } catch (error) {
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.GENERAL_ERROR,
    });
    reset_processing_state();
    report_error({
      category: SENTRY_ERROR_CATEGORIES.UI_RENDERING,
      functionName: "handle_checkout_form_submission",
      message: "Unhandled error in checkout submission",
      severity: "error",
      error,
    });
  }
};

/**
 * Orchestrates full checkout submission and payment flow
 * @returns {Promise<void>}
 */
const handle_checkout_form_submission_paytiko_only =
  async (): Promise<void> => {
    try {
      // Step 1: Initialize checkout process
      hide_all_checkout_errors();
      hide_all_coupon_errors();

      log_checkout_event("checkout", "Checkout submission started (v2)");

      // Step 2: Validate prerequisites
      const prerequisites = validate_prerequisites();

      if (!prerequisites.success) return;

      const { checkout_form } = prerequisites;

      // Step 3: Set processing state
      set_processing_state();

      log_checkout_event("checkout", "Processing state set");

      // Step 4: Extract and validate variation ID
      const checkout_form_data = new FormData(checkout_form);
      const url_params = new URLSearchParams(window.location.search);
      const variation_id = url_params.get("variation_id")!;

      if (!variation_id || isNaN(Number(variation_id))) {
        show_checkout_error({
          error_type: PAYMENT_ERROR_CODES.VARIATION_ID_INVALID,
        });
        reset_processing_state();
        log_checkout_event("checkout", "Variation id invalid", {
          variation_id,
        });
        return;
      }

      // Step 5: Validate variant availability
      const variant_result = await validate_variant({ variation_id });

      if (!variant_result.success) {
        show_checkout_error({
          error_type: variant_result.error.code,
        });

        reset_processing_state();
        log_checkout_event("checkout", "Variant validation failed", {
          variation_id,
        });
        return;
      }

      const { variant_id } = variant_result;

      // Step 6: Add product to cart
      try {
        await clear_cart();
        await add_product_to_cart({ variant_id });
        log_checkout_event("checkout", "Product added to cart", { variant_id });
      } catch (error) {
        report_error({
          category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
          functionName: "handle_checkout_form_submission_v2",
          message: "Add product to cart failed",
          severity: "error",
          network: {
            operation: "add_product_to_cart",
            headers: { "Cart-Token": get_cart_token() },
            payload: { variant_id },
          },
          error,
        });
        show_checkout_error({
          error_type: PAYMENT_ERROR_CODES.CART_UPDATE_FAILED,
        });
        reset_processing_state();
        return;
      }

      // Step 7: Validate coupon if present
      const coupon_result = await validate_coupon_if_present({ variant_id });

      if (coupon_result && !coupon_result.success) {
        reset_processing_state();
        log_checkout_event("checkout", "Coupon validation failed", {
          variant_id,
        });
        return;
      }

      // Step 8: Collect billing data
      const billing_data = collect_billing_data({
        form_data: checkout_form_data,
      });
      if (!billing_data) {
        show_checkout_error({
          error_type: PAYMENT_ERROR_CODES.BILLING_DATA_COLLECTION_FAILED,
        });
        reset_processing_state();
        log_checkout_event("checkout", "Billing data collection failed");
        return;
      }

      // Step 9: Prepare order request
      const cart_total = get_total_from_display();
      const order_is_free = cart_total <= 0;

      // Force Paytiko as payment method if not free
      const payment_method = order_is_free ? "free" : "paytiko";

      console.log("payment_method:", payment_method);

      const order_request = build_order_request({
        billing_data: billing_data!,
        payment_method,
      });

      // Step 10: Process payment
      const payment_res = order_is_free
        ? await process_free_order_payment({ order_data: order_request })
        : await process_paid_order_payment({
            order_data: order_request,
            payment_method,
          });

      if (!payment_res.success) {
        reset_processing_state();

        log_checkout_event("checkout", "Payment failed", {
          payment_method,
          order_is_free,
        });
        return;
      }

      // Step 11: Finalize checkout
      await handle_success({ order_id: payment_res.order_id, payment_method });

      log_checkout_event("checkout", "Payment succeeded", {
        order_id: payment_res.order_id,
        payment_method,
      });

      reset_processing_state();
    } catch (error) {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.GENERAL_ERROR,
      });
      reset_processing_state();
      report_error({
        category: SENTRY_ERROR_CATEGORIES.UI_RENDERING,
        functionName: "handle_checkout_form_submission_v2",
        message: "Unhandled error in checkout submission",
        severity: "error",
        error,
      });
    }
  };

/**
 * Handles payment method radio change to show corresponding section
 * @param {Event} event - Change event from radio input
 * @returns {Promise<void>}
 */
const handle_payment_method_change = async (event: Event): Promise<void> => {
  const target = event.target as HTMLInputElement;
  if (!target?.value) return;

  const payment_method = target.value;
  hide_all_payment_sections();
  show_payment_section({ gateway_id: payment_method });
  initialize_selected_gateway_ui({ gateway_id: payment_method });
};

/**
 * Wires up form validation and checkout UI event listeners
 * @returns {void}
 */
const initialize_event_listeners = (): void => {
  const form = document.getElementById("checkout-form") as HTMLFormElement;
  if (form?.dataset.listenersAttached) return;

  const account_selection_form = document.getElementById(
    "account-selection-form",
  ) as HTMLFormElement;

  const place_order_btn = document.getElementById(
    "place-order-btn",
  ) as HTMLButtonElement;

  const payment_radios = document.querySelectorAll(
    'input[name="payment_method"]',
  ) as NodeListOf<HTMLInputElement>;

  if (form) setup_real_time_field_validation(form);

  if (account_selection_form)
    setup_real_time_field_validation(account_selection_form);

  if (form && place_order_btn) {
    place_order_btn.addEventListener(
      "click",
      handle_checkout_form_submission_paytiko_only,
    );
  }

  if (payment_radios?.length > 0) {
    payment_radios.forEach((radio: HTMLInputElement) => {
      radio.addEventListener("change", handle_payment_method_change);
    });
  }
  setup_billing_country_restriction_handler();

  if (form) (form as any).listeners_attached = true;
};

/**
 * Parses URL for error params and surfaces checkout errors
 * @returns {void}
 */
const check_url_error_params = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (!error) return;
    hide_all_checkout_errors();

    if (error === "BLOCKED.") {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.PAYMENT_GATEWAY_BLOCKED,
      });
    } else if (error === "PAYMENT_FAILED") {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.PAYMENT_FAILED,
      });
    } else if (error === "AUTHENTICATION_UNSUCCESSFUL.") {
      show_checkout_error({
        error_type: PAYMENT_ERROR_CODES.PAYMENT_INTENT_AUTHENTICATION_FAILURE,
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    window.history.replaceState({}, "", url.toString());
  } catch {
    console.error("Failed to parse URL error params");
    report_error({
      category: SENTRY_ERROR_CATEGORIES.UI_RENDERING,
      functionName: "check_url_error_params",
      message: "Failed to parse URL error params",
      severity: "warning",
    });
    show_checkout_error({
      error_type: PAYMENT_ERROR_CODES.REDIRECT_PARSE_FAILED,
    });
  }
};

/**
 * Initializes checkout page client: session, listeners, and restrictions
 * @returns {Promise<void>}
 */
export const init_checkout_page_client = async (): Promise<void> => {
  try {
    await initialize_cart_session();
    log_checkout_event("checkout", "Cart session initialized");
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
      functionName: "init",
      message: "Failed to initialize cart session",
      severity: "error",
      error,
      network: { operation: "initialize_cart_session" },
    });
  }

  try {
    initialize_event_listeners();
    log_checkout_event("checkout", "Event listeners initialized");
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.UI_RENDERING,
      functionName: "init",
      message: "Failed to initialize event listeners",
      severity: "warning",
      error,
    });
  }

  try {
    await check_url_region_restrictions();
    log_checkout_event("checkout", "Region restrictions checked");
  } catch (error) {
    report_error({
      category: SENTRY_ERROR_CATEGORIES.API_COMMUNICATION,
      functionName: "init",
      message: "Failed to check URL region restrictions",
      severity: "warning",
      error,
    });
  }

  check_url_error_params();
};
